<template>
  <div class="withdraw-options">
    <div v-if="!loading" class="withdraw-options__list">
      <div class="withdraw-options__wallet">
        <CurrencyFlag :currency="currency" />
        <div class="withdraw-options__wallet-info">
          <span class="withdraw-options__label">Withdrawing from</span>
          <span class="withdraw-options__wallet-name">{{ currency }} Wallet</span>
        </div>
        <div class="withdraw-options__wallet-balance">
          <span class="withdraw-options__label">Available</span>
          <span class="withdraw-options__wallet-amount">{{ formatAmountToDollar(walletBalanceAmount, 2, currency) }}</span>
        </div>
      </div>

      <section class="withdraw-options__section">
        <div>
          <h3 class="withdraw-options__section-title">Select bank account</h3>
          <p class="withdraw-options__section-desc">Choose the bank account to receive your funds</p>
        </div>

        <div v-if="currencyBankDetails.length > 0" class="withdraw-options__group">
          <button
            v-for="bank in currencyBankDetails"
            :key="bank._id"
            type="button"
            class="withdraw-options__row"
            @click="selectBank(bank)"
          >
            <span class="withdraw-options__avatar" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 7L9 3L15 7V9H3V7Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" />
                <path d="M4 9V13M7 9V13M11 9V13M14 9V13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                <path d="M2 15H16" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </span>
            <span class="withdraw-options__row-info">
              <span class="withdraw-options__row-title">{{ bank.name }}</span>
              <span class="withdraw-options__row-meta">{{ bankDetailsLine(bank) }}</span>
            </span>
            <svg class="withdraw-options__arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </div>

        <p v-else class="withdraw-options__empty">
          You have no saved {{ currency }} bank accounts yet. Add one to withdraw.
        </p>

        <button
          type="button"
          class="withdraw-options__add"
          @click="clicked('Add Bank')"
        >
          <span class="withdraw-options__add-icon" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 3V13M3 8H13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </span>
          <span class="withdraw-options__row-info">
            <span class="withdraw-options__row-title">Add bank account</span>
            <span class="withdraw-options__row-meta">Save a new account for withdrawals</span>
          </span>
        </button>
      </section>
    </div>
    <SmallLoader v-if="loading" />
  </div>
</template>

<script>
import CurrencyFlag from "@/components/wallet/CurrencyFlag.vue";
import { formatAmountToDollar, getCurrencySymbol } from "@/utils/helpers.js";
import { setItem } from "@/utils/storage.js";
import countryRegionData from "@/utils/js/countryRegionData";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "WithdrawOptionsForm",

  components: { CurrencyFlag },

  props: {
    bankDetails: { type: Array, default: () => [], required: true },
    minimumWithdrawal: { type: Number, default: () => 0, required: true },
  },

  data() {
    return {
      payload: {
        amount: "",
        bank_id: "",
        currency: "",
        tokens: "",
      },
    };
  },

  computed: {
    ...mapState({
      currency: (state) => state.organisationModule.currency,
      loading: (state) => state.smallLoading,
    }),
    ...mapGetters("organisationModule", ["walletBalance"]),
    currencyBankDetails() {
      return this.bankDetails.filter((bank) => bank.currency === this.currency);
    },
    walletBalanceAmount() {
      const wallet = this.walletBalance?.find((w) => w.currency === this.currency);
      return wallet?.balance ?? 0;
    },
  },

  methods: {
    ...mapActions("organisationModule", [
      "setCurrency",
      "setWithdrawMethod",
      "getExchangeRate",
    ]),

    getCurrencySymbol,
    formatAmountToDollar,

    // e.g. "Ghana · ••••8760"
    bankDetailsLine(bank) {
      const country = countryRegionData.find((c) => c.countryShortCode === bank.country);
      return [country?.countryName, this.maskAccountNumber(bank.number)]
        .filter(Boolean)
        .join(" · ");
    },

    maskAccountNumber(number) {
      if (!number) return "";
      const str = String(number);
      return "••••" + str.slice(-4);
    },

    async clicked(card) {
      switch (card) {
        case "Other GetEquity Wallet":
          this.payload.currency = this.currency;
          setItem("withdrawalPayload", this.payload);
          this.setWithdrawMethod(card);
          this.$emit("storeData", card);
          break;
        case "Add Bank":
          this.$emit("storeData", card);
          break;
        default:
          break;
      }
    },

    async selectBank(bank) {
      this.payload.bank_id = bank._id;
      this.payload.currency = bank.currency;
      this.setCurrency(this.payload.currency);
      setItem("withdrawalPayload", this.payload);
      const exchangeQuery = { from: this.payload.currency, to: "USD" };
      const response = await this.getExchangeRate(exchangeQuery);
      if (response) {
        this.$emit("storeData", "withdrawOptionsForm");
        this.setWithdrawMethod("Bank");
      }
    },

    storeData(payload) {
      this.selectBank({ _id: payload.bank_id, currency: payload.currency });
    },
  },
};
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F8FAFC;
$bg-hover: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #6366F1;
$accent-light: #EEF2FF;

.withdraw-options {
  &__list {
    display: flex;
    flex-direction: column;
    gap: 28px;
  }

  &__label {
    font-size: 12px;
    color: $text-muted;
  }

  &__wallet {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: $bg-subtle;
    border: 1px solid $border-color;
    border-radius: 12px;
  }

  &__wallet-info,
  &__wallet-balance {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__wallet-info {
    flex: 1;
  }

  &__wallet-balance {
    align-items: flex-end;
    text-align: right;
  }

  &__wallet-name,
  &__wallet-amount {
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
  }

  &__wallet-amount {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__section-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: $text-primary;
  }

  &__section-desc {
    margin: 2px 0 0;
    font-size: 13px;
    color: $text-muted;
  }

  &__group {
    border: 1px solid $border-color;
    border-radius: 12px;
    overflow: hidden;
  }

  &__row,
  &__add {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 14px 16px;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:focus-visible {
      outline: 2px solid $accent-primary;
      outline-offset: -2px;
    }
  }

  &__row {
    background: $bg-card;
    border: none;

    & + & {
      border-top: 1px solid $border-color;
    }

    &:hover {
      background: $bg-hover;
    }
  }

  &__avatar,
  &__add-icon {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__avatar {
    background: $bg-hover;
    color: $text-secondary;
  }

  &__row-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__row-title {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__row-meta {
    font-size: 13px;
    color: $text-muted;
    font-variant-numeric: tabular-nums;
  }

  &__arrow {
    color: $text-muted;
    flex-shrink: 0;
    transition: transform 0.15s ease;
  }

  &__row:hover &__arrow {
    transform: translateX(2px);
  }

  &__add {
    background: transparent;
    border: 1px dashed $border-color;
    border-radius: 12px;

    &:hover {
      background: $accent-light;
      border-color: $accent-primary;
    }
  }

  &__add-icon {
    background: $accent-light;
    color: $accent-primary;
  }

  &__empty {
    margin: 0;
    padding: 16px;
    font-size: 13px;
    line-height: 1.5;
    color: $text-muted;
    background: $bg-subtle;
    border-radius: 12px;
  }
}
</style>
